import { api } from "@/lib/api";
import type { Pagination } from "@/lib/types";

/** 工程の費目区分。API の ProcessCategory（lib/enums/process_category.py）と一致させる。 */
export type ProcessCategory =
  "material" | "machining" | "fabrication" | "treatment" | "other";

/**
 * 費目区分の表示名。客先の見積依頼の回答欄に対応する（平田機工は材料費・
 * 機械加工費・製缶費・処理費・その他費用の 5 欄）。
 *
 * 並びは API の ENUM の定義順と揃えてある。この順で並べたものが、そのまま
 * 客先の回答欄の順序になる。
 */
export const PROCESS_CATEGORY_LABEL: Record<ProcessCategory, string> = {
  material: "材料費",
  machining: "機械加工費",
  fabrication: "製缶費",
  treatment: "処理費",
  other: "その他",
};

/** 費目区分の選択肢（表示順は PROCESS_CATEGORY_LABEL の定義順）。 */
export const PROCESS_CATEGORY_OPTIONS = (
  Object.keys(PROCESS_CATEGORY_LABEL) as ProcessCategory[]
).map((key) => ({ key, label: PROCESS_CATEGORY_LABEL[key] }));

/** 工程（API の ProcessResponse に対応）。 */
export interface Process {
  process_id: number;
  process_code: string;
  process_name: string;
  /**
   * 基本単価（時間単価）。見積で時間を入れたときの既定値。時間で見積らない
   * 工程（外注費など）では null。
   *
   * ここを変えても**過去の見積金額は動かない**。見積側が採用時の単価と金額を
   * 控えているため。金額は API が NUMERIC を文字列で返す（浮動小数の誤差を
   * 持ち込まないため）。
   */
  base_price: string | null;
  /**
   * 費目区分。客先への回答は費目別の内訳を求められるため、工程別の内訳を
   * この区分で畳んで埋める。区分を決めずに登録された工程は "other"。
   */
  category: ProcessCategory;
  /** 使用停止。true は見積の工程候補から外れる（過去の見積の内訳は残る）。 */
  disabled: boolean;
  created_at: string | null;
  updated_at: string | null;
}

export interface ProcessListResponse {
  items: Process[];
  pagination: Pagination;
}

export interface ProcessListParams {
  keyword?: string;
  /** 未指定なら使用中・停止中の両方。見積の工程候補では false を渡す。 */
  disabled?: boolean;
  /** 未指定なら全区分。 */
  category?: ProcessCategory;
  sortKey?: string;
  ascending?: boolean;
  limit: number;
  offset: number;
}

export function listProcesses(params: ProcessListParams): Promise<ProcessListResponse> {
  const query = new URLSearchParams();
  query.set("limit", String(params.limit));
  query.set("offset", String(params.offset));
  if (params.keyword) query.set("keyword", params.keyword);
  if (params.disabled != null) query.set("disabled", String(params.disabled));
  if (params.category) query.set("category", params.category);
  if (params.sortKey) query.set("sort_key", params.sortKey);
  if (params.ascending != null) query.set("ascending", String(params.ascending));
  return api.get<ProcessListResponse>(`/processes?${query}`);
}

/** 登録・更新で送れる項目（process_id や日時は送らない）。 */
export type ProcessInput = Partial<
  Pick<
    Process,
    "process_code" | "process_name" | "base_price" | "category" | "disabled"
  >
>;

export function createProcess(input: ProcessInput): Promise<Process> {
  return api.post<Process>("/processes", input);
}

/** 部分更新。渡した項目だけ反映される。 */
export function updateProcess(
  processId: number,
  input: ProcessInput,
): Promise<Process> {
  return api.patch<Process>(`/processes/${processId}`, input);
}

export function deleteProcess(processId: number): Promise<void> {
  return api.delete<void>(`/processes/${processId}`);
}
